// LT-511: month-view calendar. Pure component — receives availability
// set and selection, emits onSelect when a day with availability is
// clicked.
import React from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MONTHS, WEEKDAYS_SHORT } from '../formatters';

interface Props {
  /** First day of the month to render. */
  viewMonth: Date;
  setViewMonth: (d: Date) => void;
  /** Set of YYYY-MM-DD strings that have one or more available slots. */
  available: Set<string>;
  /** Currently selected YYYY-MM-DD, or null. */
  selected: string | null;
  /** "Today" in the host timezone, YYYY-MM-DD. */
  today: string;
  onSelect: (ymd: string) => void;
}

function addMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}
function daysInMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}
function toYmd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const MiniCalendar: React.FC<Props> = ({
  viewMonth, setViewMonth, available, selected, today, onSelect,
}) => {
  const y = viewMonth.getFullYear();
  const m = viewMonth.getMonth();
  const startWeekday = new Date(y, m, 1).getDay();
  const total = daysInMonth(y, m);
  const cells: Array<number | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-50">
          {MONTHS[m]} {y}
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label="Previous month"
            className="w-7 h-7 inline-flex items-center justify-center rounded-ds-md text-slate-400 hover:bg-white/5 hover:text-slate-50 transition-colors"
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            aria-label="Next month"
            className="w-7 h-7 inline-flex items-center justify-center rounded-ds-md text-slate-400 hover:bg-white/5 hover:text-slate-50 transition-colors"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS_SHORT.map((w, i) => (
          <div
            key={i}
            className="h-7 flex items-center justify-center text-[11px] font-medium uppercase tracking-wider text-slate-500"
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="h-[38px]" />;
          const ymd = toYmd(y, m, d);
          const isAvailable = available.has(ymd);
          const isSelected = ymd === selected;
          const isToday = ymd === today;
          return (
            <button
              key={i}
              type="button"
              disabled={!isAvailable}
              aria-pressed={isSelected}
              aria-label={`${MONTHS[m]} ${d}`}
              onClick={() => isAvailable && onSelect(ymd)}
              className={clsx(
                'relative h-[38px] flex items-center justify-center rounded-ds-md text-[13px] transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
                !isAvailable && 'text-slate-600 cursor-default',
                isAvailable && !isSelected && 'font-medium text-slate-50 hover:bg-accent-tint hover:text-accent-text cursor-pointer',
                isSelected && 'bg-accent text-white font-semibold hover:bg-accent-hover',
              )}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {d}
              {isAvailable && !isSelected && (
                <span
                  className="absolute bottom-1 w-1 h-1 rounded-full bg-accent"
                  aria-hidden="true"
                />
              )}
              {isToday && !isAvailable && (
                <span
                  className="absolute bottom-1 w-1 h-1 rounded-full bg-current"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MiniCalendar;
